/**
 * ModeConfigForm — Reusable mode configuration form for one pendant mode.
 *
 * Renders the form fields for configuring a single mode (Idle, Panic, or Accepted):
 *   - Text Size select (Standard 3-line vs Larger 2-line)
 *   - Line 1 config: Data Type select, Custom Text input, Text After toggle
 *   - Line 2 config: same
 *   - Line 3 config: same (hidden when text_size = 2)
 *
 * This component is stateless — all state lives in the parent ProfileDrawer.
 * Changes are bubbled up via the `onChange(fieldName, value)` callback.
 *
 * Props:
 *   mode      — object with all line configs for one mode
 *   onChange  — callback: (fieldName, value) => void
 *   dataTypes — array of { data_type_id, data_type_name }
 *   errors    — per-field error messages (keyed by field name)
 */

const ModeConfigForm = ({ mode, onChange, dataTypes, errors }) => {
    const textSize = mode?.text_size || 1;
    const isThreeLine = textSize === 1;

    const clearError = (field) => {
        if (errors?.[field]) {
            onChange(`_clearError:${field}`, null);
        }
    };

    // ======================================================================
    // Line config renderer — avoids triplicating JSX
    // ======================================================================
    const renderLineConfig = (lineNum) => {
        const prefix = lineNum === 1 ? 'line_one' : lineNum === 2 ? 'line_two' : 'line_three';
        const dataTypeField = `${prefix}_data_type_id`;
        const textField = `${prefix}_text`;
        const suffixField = `${prefix}_text_suffix`;

        const dataTypeValue = mode?.[dataTypeField] || '';
        const textValue = mode?.[textField] || '';
        const suffixValue = mode?.[suffixField] || false;
        const errorKey = dataTypeField;

        return (
            <div key={lineNum} className="pp-line-config">
                <div className="pp-line-label">Line {lineNum}</div>
                {/* Data Type + Custom Text side by side */}
                <div className="pp-line-row">
                    <div className="dp-drawer__field">
                        <label className="dp-drawer__label">
                            Data Type <span className="dp-drawer__required">*</span>
                        </label>
                        <select
                            className={`dp-drawer__input${errors?.[errorKey] ? ' is-invalid' : ''}`}
                            value={dataTypeValue}
                            onChange={(e) => {
                                onChange(dataTypeField, e.target.value ? parseInt(e.target.value, 10) : '');
                                clearError(errorKey);
                            }}
                        >
                            <option value="">Select...</option>
                            {(dataTypes || []).map((dt) => (
                                <option key={dt.data_type_id} value={dt.data_type_id}>
                                    {dt.data_type_name}
                                </option>
                            ))}
                        </select>
                        {errors?.[errorKey] ? <p className="dp-drawer__error">{errors[errorKey]}</p> : null}
                    </div>

                    <div className="dp-drawer__field">
                        <label className="dp-drawer__label">Custom Text</label>
                        <input
                            className="dp-drawer__input"
                            type="text"
                            value={textValue}
                            onChange={(e) => onChange(textField, e.target.value)}
                            placeholder="Optional text..."
                            maxLength={textSize === 1 ? 20 : 14}
                        />
                    </div>
                </div>
                {/* Text placement toggle on its own row */}
                <div className="pp-suffix-toggle">
                    <label className="dp-toggle">
                        <input
                            type="checkbox"
                            checked={!!suffixValue}
                            onChange={(e) => onChange(suffixField, e.target.checked)}
                        />
                        <span className="dp-toggle__track" />
                    </label>
                    <span className="pp-suffix-label">Place custom text after data</span>
                </div>
            </div>
        );
    };

    // ======================================================================
    // Render
    // ======================================================================
    return (
        <div className="pp-mode-config">
            {/* Text Size */}
            <div className="dp-drawer__field">
                <label className="dp-drawer__label">Text Size</label>
                <select
                    className="dp-drawer__input"
                    value={textSize}
                    onChange={(e) => onChange('text_size', parseInt(e.target.value, 10))}
                >
                    <option value={1}>Standard 3-line (20 char)</option>
                    <option value={2}>Larger 2-line (14 char)</option>
                </select>
                <p style={{ marginTop: 'var(--space-1, 0.25rem)', fontSize: 'var(--font-size-xs, 0.75rem)', color: 'var(--color-text-muted, #6b7280)' }}>
                    {isThreeLine
                        ? 'Three display lines, max 20 characters each.'
                        : 'Two display lines with larger text, max 14 characters each.'}
                </p>
            </div>

            {/* Line configs */}
            {renderLineConfig(1)}
            {renderLineConfig(2)}
            {isThreeLine ? renderLineConfig(3) : null}
        </div>
    );
};

export default ModeConfigForm;
